import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { Grid, Stack } from '@mantine/core';
import { KiloCheckoutAccordion, KiloCheckoutAccordionItem } from 'components/KiloAccordion';
import { SetupPaymentMethodProvider } from 'components/payment-methods/SetupPaymentMethod';
import BillingPlanSelect from 'components/SalesPortal/BillingPlanSelect';
import { useLocalization } from 'context/localization-context';
import { useTracking } from 'hooks/mixpanel/useTracking';
import useSalesPortalCheckoutProgress from 'hooks/salesPortal/useSalesPortalCheckoutProgress';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
  BillingPlanRetrieve,
  BillingPlanWithInstallments,
  SalesPortalCheckoutProgressWrite,
  SalesPortalPackageRetrieve,
} from 'types';
import { preserveQueryParams } from 'utils/navigation';

import { CheckoutProvider, useCheckout, WhoIsThisFormOption } from './CheckoutContext';
import { CheckoutStep, CHECKOUT_STEP_LABELS } from './checkoutFlowStates';
import AccountStep from './components/AccountStep';
import AgreementWaiver from './components/AgreementWaiver';
import AthleteInformation from './components/AthleteInformation';
import BillingPlanError from './components/BillingPlanError';
import Confirmation from './components/Confirmation';
import KiloClassScheduleError from './components/KiloClassError';
import OrderSummaryPanel from './components/OrderSummary';
import PaymentInformation from './components/PaymentInformation';
import ResumeBanner from './components/ResumeBanner';
import WhoIsThisFor from './components/WhoIsThisFor';
import useKiloClassGate from './useKiloClassGate';

interface CheckoutProps {
  packageData: SalesPortalPackageRetrieve;
  billingPlan: BillingPlanRetrieve;
}

const WHO_IS_THIS_FOR_SUMMARY: Record<WhoIsThisFormOption, string> = {
  myself: 'Myself',
  subAccount: 'An existing linked or dependent profile',
  newLinkedAccount: 'A new linked profile',
  newDependentAccount: 'A new dependent profile',
};

const BillingPlanStepContent = (): JSX.Element => {
  const { packageData, billingPlan, completeStep } = useCheckout();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { salesPortalSlug } = useParams<{ salesPortalSlug: string }>();

  // Public plans are listed; a PRIVATE plan reached by direct link stays available
  // as the current selection without being advertised alongside them.
  const selectablePlans = useMemo(() => {
    const publicPlans = packageData.billingPlans.filter(plan => plan.salesPortalStatus === 'PUBLIC');
    if (publicPlans.some(plan => plan.billingPlanId === billingPlan.billingPlanId)) return publicPlans;
    const currentPlan = packageData.billingPlans.find(
      plan => plan.billingPlanId === billingPlan.billingPlanId,
    );
    return currentPlan ? [currentPlan, ...publicPlans] : publicPlans;
  }, [packageData.billingPlans, billingPlan.billingPlanId]);

  const handleSelect = useCallback(
    (plan: BillingPlanWithInstallments): void => {
      if (plan.billingPlanId === billingPlan.billingPlanId) {
        completeStep('billingPlan');
        return;
      }
      // Same route pattern, new plan id: the page stays mounted, the plan query
      // refetches, and CheckoutContext invalidates the downstream steps.
      navigate(
        preserveQueryParams(
          `/sales-portal/${salesPortalSlug}/${packageData.packageId}/${plan.billingPlanId}`,
          searchParams,
        ),
        { replace: true },
      );
    },
    [
      billingPlan.billingPlanId,
      completeStep,
      navigate,
      salesPortalSlug,
      packageData.packageId,
      searchParams,
    ],
  );

  return (
    <BillingPlanSelect
      billingPlans={selectablePlans}
      gym={packageData.gym}
      selectedBillingPlanId={billingPlan.billingPlanId}
      onSelect={handleSelect}
    />
  );
};

const CheckoutAccordion = (): JSX.Element => {
  const {
    packageData,
    billingPlan,
    form,
    steps,
    openStep,
    setOpenStep,
    isStepCompleted,
    isStepUnlocked,
    isStepInvalidated,
    isStepEditable,
    expandStep,
    loggedInAthleteEmail,
    agreement,
  } = useCheckout();
  const { formatCurrency } = useLocalization(packageData.gym?.country, {
    isTaxInclusive: packageData.gym?.isTaxInclusive,
  });

  const stepSummary = useCallback(
    (step: CheckoutStep): string | undefined => {
      switch (step) {
        case 'billingPlan':
          return `${billingPlan.name} — ${formatCurrency((billingPlan.totalPrice || 0) / 100)}`;
        case 'account':
          return loggedInAthleteEmail || undefined;
        case 'whoIsThisFor':
          return WHO_IS_THIS_FOR_SUMMARY[form.values.whoIsThisFor];
        case 'athleteInformation': {
          const { firstName, lastName, email } = form.values.athleteInformation;
          const name = `${firstName} ${lastName}`.trim();
          return name ? `${name}${email ? ` — ${email}` : ''}` : undefined;
        }
        case 'agreementWaiver': {
          if (!agreement.preview?.hasContract) return 'Waiver signed';
          return agreement.waiverNeeded ? 'Agreement & waiver signed' : 'Agreement signed';
        }
        case 'payment':
          return form.values.paymentMethod.stripeConfirmationToken
            ? 'New payment method added'
            : 'Payment method selected';
        default:
          return undefined;
      }
    },
    [
      billingPlan,
      formatCurrency,
      loggedInAthleteEmail,
      form.values,
      agreement.preview,
      agreement.waiverNeeded,
    ],
  );

  const stepContent = (step: CheckoutStep): JSX.Element | null => {
    switch (step) {
      case 'billingPlan':
        return <BillingPlanStepContent />;
      case 'account':
        return <AccountStep />;
      case 'whoIsThisFor':
        return <WhoIsThisFor />;
      case 'athleteInformation':
        return <AthleteInformation />;
      case 'agreementWaiver':
        return <AgreementWaiver />;
      case 'payment':
        return <PaymentInformation />;
      default:
        return null;
    }
  };

  const items: KiloCheckoutAccordionItem[] = steps.map((step, index) => ({
    value: step,
    label: CHECKOUT_STEP_LABELS[step],
    stepNumber: index + 1,
    completed: isStepCompleted(step),
    locked: !isStepUnlocked(step),
    summary: isStepCompleted(step) ? stepSummary(step) : undefined,
    warning: isStepInvalidated(step) ? 'Billing plan changed — please re-confirm this step.' : null,
    onEdit: isStepEditable(step) ? () => expandStep(step) : undefined,
    children: stepContent(step),
  }));

  return (
    <KiloCheckoutAccordion
      items={items}
      value={openStep}
      onChange={value => (value ? expandStep(value as CheckoutStep) : setOpenStep(null))}
    />
  );
};

const CheckoutController = (): JSX.Element => {
  const { packageData, phase } = useCheckout();
  useKiloClassGate();

  if (phase === 'confirmation') {
    return <Confirmation />;
  }

  if (phase === 'kiloClassError') {
    return <KiloClassScheduleError />;
  }

  if (phase === 'billingPlanError') {
    return <BillingPlanError />;
  }

  return (
    <SetupPaymentMethodProvider gymId={packageData.gym.gymId}>
      <ResumeBanner />
      <Grid gutter='lg' align='start'>
        <Grid.Col span={12} md={7}>
          <CheckoutAccordion />
        </Grid.Col>
        <Grid.Col span={12} md={5}>
          <Stack
            data-cy='orderSummaryColumn'
            sx={theme => ({ [theme.fn.largerThan('md')]: { position: 'sticky', top: '1rem' } })}
          >
            <OrderSummaryPanel />
          </Stack>
        </Grid.Col>
      </Grid>
    </SetupPaymentMethodProvider>
  );
};

const Checkout = ({ packageData, billingPlan }: CheckoutProps): JSX.Element => {
  const { trackSalesPortalCheckoutStepCompleted, trackSalesPortalPurchaseStarted } = useTracking();
  const { mutate: saveCheckoutProgress } = useSalesPortalCheckoutProgress({
    gym: packageData.gym.gymId,
    packageId: packageData.packageId,
    billingPlanId: billingPlan.billingPlanId,
  });

  useEffect(() => {
    trackSalesPortalPurchaseStarted(
      packageData.gym.gymId,
      packageData.packageId,
      packageData.type,
      billingPlan.billingPlanId,
      billingPlan.numberOfInstallments,
      'public',
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [packageData.packageId, billingPlan.billingPlanId]);

  const handleStepCompleted = useCallback(
    (step: CheckoutStep): void => {
      trackSalesPortalCheckoutStepCompleted(step, packageData.gym.gymId, packageData.packageId);
    },
    [trackSalesPortalCheckoutStepCompleted, packageData.gym.gymId, packageData.packageId],
  );

  // Server-side progress save (spec Item 11). Best-effort: a failed save is retried by
  // the next change, and the buyer is never interrupted over it.
  const handleProgressSave = useCallback(
    (payload: SalesPortalCheckoutProgressWrite): void => {
      saveCheckoutProgress(payload);
    },
    [saveCheckoutProgress],
  );

  const [checkoutRun, setCheckoutRun] = useState(0);
  const handleSignedOut = useCallback((): void => {
    setCheckoutRun(run => run + 1);
  }, []);

  return (
    <CheckoutProvider
      key={checkoutRun}
      packageData={packageData}
      billingPlan={billingPlan}
      onStepCompleted={handleStepCompleted}
      onProgressSave={handleProgressSave}
      onSignedOut={handleSignedOut}
    >
      <CheckoutController />
    </CheckoutProvider>
  );
};

export default Checkout;
